import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Skeleton } from 'boneyard-js/react'
import {
  type ClipboardEvent,
  type KeyboardEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { ColumnVisibilityToggle } from '../../../components/ColumnVisibilityToggle'
import { UnsavedChangesDialog } from '../../../components/UnsavedChangesDialog'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DatePicker } from '../../../components/ui/date-picker'
import { Input } from '../../../components/ui/input'
import { MonthPicker } from '../../../components/ui/month-picker'
import { useColumnVisibility } from '../../../hooks/useColumnVisibility'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges'
import { supabase } from '../../../lib/supabase'
import { useAuthStore } from '../../../stores/authStore'
import { SavePreviewModal } from './SavePreviewModal'
import { SnapshotCompareModal } from './SnapshotCompareModal'
import { SyncUtilitiesPreviewModal } from './SyncUtilitiesPreviewModal'
import type {
  MonthlyContractRow,
  MonthlyRowEntry,
  MonthlyUtilityFormValues,
  MonthlyUtilityRow,
  SnapshotDetailRow,
} from './interfaces/MonthlyRecord'
import { ROOM_STATUS_OPTIONS, ROOM_TYPE_OPTIONS } from './monthlyRecordOptions'
import {
  calcTotals,
  getMonthFromSearchParams,
  isMeterReadingValid,
  monthEnd,
  monthStart,
  toYearMonth,
} from './monthlyRecordsHelpers'
import {
  type MonthlySavePlan,
  buildMonthlySavePlan,
  reconcileSavedRows,
} from './monthlyRecordsSave'
import { saveMonthlyRecords } from './monthlyRecordsService'
import {
  ROOM_SYNC_FIELDS,
  type RoomSyncField,
  type SnapshotComparableField,
  applyRoomFieldsToRows,
  applySourceFieldsToRows,
  buildLiveMonthlyRows,
  buildSnapshotMonthlyRows,
} from './monthlyRecordsState'

type SnapshotData = { id: string | null; rows: SnapshotDetailRow[] }
const EMPTY_SNAPSHOT_ROWS: SnapshotDetailRow[] = []

const HIDDEN_COLS_KEY = 'monthly-records-hidden-cols'
const ROOM_SYNC_FIELD_LABELS: Record<RoomSyncField, string> = {
  price_monthly: 'Rent',
  internet_fee: 'Internet',
  furniture_fee: 'Furniture',
  maintenance_fee: 'Maintain',
  other_fee: 'Other',
  other_fee_label: 'Other Label',
  room_type: 'Room Type',
  room_status: 'Room Status',
  room_remark: 'Room Note',
  repair_details: 'Repair Details',
  keycards: 'Keycards',
}
type RoomSyncSelection = RoomSyncField | 'elec_prev' | 'water_prev' | 'all'

const COLUMN_DEFS: { id: string; label: string }[] = [
  { id: 'tenant', label: 'Tenant' },
  { id: 'rent', label: 'Rent (฿)' },
  { id: 'elec_unit_price', label: 'Elec ฿/Unit' },
  { id: 'elec_prev', label: 'Elec Prev' },
  { id: 'elec_curr', label: 'Elec Curr' },
  { id: 'elec_units', label: 'Elec Units' },
  { id: 'elec_total', label: 'Elec Total ฿' },
  { id: 'water_unit_price', label: 'Water ฿/Unit' },
  { id: 'water_prev', label: 'Water Prev' },
  { id: 'water_curr', label: 'Water Curr' },
  { id: 'water_units', label: 'Water Units' },
  { id: 'water_total', label: 'Water Total ฿' },
  { id: 'internet', label: 'Internet' },
  { id: 'furniture', label: 'Furniture' },
  { id: 'maintenance', label: 'Maintain' },
  { id: 'other', label: 'Other' },
  { id: 'other_fee_label', label: 'Other Label' },
  { id: 'grand_total', label: 'Grand Total ฿' },
  { id: 'date', label: 'Date' },
  { id: 'payment', label: 'Payment' },
  { id: 'remark', label: 'Remark' },
  { id: 'status', label: 'Status' },
  // Room metadata (read-only)
  { id: 'room_type', label: 'Room Type' },
  { id: 'room_status', label: 'Room Status' },
  { id: 'room_remark', label: 'Room Note' },
  { id: 'repair_details', label: 'Repair Details' },
  { id: 'keycards', label: 'Keycards' },
]

const rowSchema = z
  .object({
    elec_unit_price: z
      .string()
      .min(1, 'Required')
      .pipe(z.coerce.number().min(0)),
    elec_prev_reading: z
      .string()
      .min(1, 'Required')
      .pipe(z.coerce.number().int().min(0)),
    elec_curr_reading: z
      .string()
      .min(1, 'Required')
      .pipe(z.coerce.number().int().min(0)),
    water_unit_price: z
      .string()
      .min(1, 'Required')
      .pipe(z.coerce.number().min(0)),
    water_prev_reading: z
      .string()
      .min(1, 'Required')
      .pipe(z.coerce.number().int().min(0)),
    water_curr_reading: z
      .string()
      .min(1, 'Required')
      .pipe(z.coerce.number().int().min(0)),
    record_date: z.string().min(1, 'Record date required'),
    payment_status: z.enum(['paid', 'unpaid']),
    remark: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      typeof data.elec_prev_reading === 'number' &&
      typeof data.elec_curr_reading === 'number' &&
      !isMeterReadingValid(data.elec_prev_reading, data.elec_curr_reading)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['elec_curr_reading'],
        message: 'Reading decreased (not a rollover)',
      })
    }
    if (
      typeof data.water_prev_reading === 'number' &&
      typeof data.water_curr_reading === 'number' &&
      !isMeterReadingValid(data.water_prev_reading, data.water_curr_reading)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['water_curr_reading'],
        message: 'Reading decreased (not a rollover)',
      })
    }
  })

// ── component ──────────────────────────────────────────────────────────────

export default function MonthlyRecordsPage() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  const [searchParams, setSearchParams] = useSearchParams()
  // Get month from query string, fallback to current month
  const initialMonth = getMonthFromSearchParams(searchParams)
  const [selectedMonth, setSelectedMonth] = useState<string>(initialMonth)
  const [appliedMonth, setAppliedMonth] = useState<string>(initialMonth)
  const [editor, setEditor] = useState<{
    month: string | null
    snapshotId: string | null
    rows: MonthlyRowEntry[]
  }>({ month: null, snapshotId: null, rows: [] })
  const rows = editor.rows
  const setRows = useCallback((update: SetStateAction<MonthlyRowEntry[]>) => {
    setEditor((prev) => ({
      ...prev,
      rows: typeof update === 'function' ? update(prev.rows) : update,
    }))
  }, [])
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [previewRows, setPreviewRows] = useState<MonthlyRowEntry[]>([])
  const [saveResult, setSaveResult] = useState<{
    saved: number
    errors: number
  } | null>(null)
  const [snapshotResult, setSnapshotResult] = useState<{
    snapshotId: string
    rowCount: number
  } | null>(null)
  const [bulkElecPrice, setBulkElecPrice] = useState('')
  const [bulkWaterPrice, setBulkWaterPrice] = useState('')
  const [selectedRoomSyncField, setSelectedRoomSyncField] =
    useState<RoomSyncSelection>('keycards')
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [showSyncUtilitiesModal, setShowSyncUtilitiesModal] = useState(false)
  const [showCompareModal, setShowCompareModal] = useState(false)
  const [roomSearch, setRoomSearch] = useState('')
  const showHeader = searchParams.get('header') !== 'hide'
  const debouncedRoomSearch = useDebouncedValue(roomSearch, 500)
  const normalizedRoomSearch = debouncedRoomSearch.trim()

  const isDirty = rows.some((r) => r.dirty)
  const { isBlocked, proceed, reset } = useUnsavedChanges(isDirty || isSaving, {
    shouldBlock: ({ currentLocation, nextLocation }) => {
      if (currentLocation.pathname !== nextLocation.pathname) return true
      const currMonth = new URLSearchParams(currentLocation.search).get('month')
      const nextMonth = new URLSearchParams(nextLocation.search).get('month')
      return currMonth !== nextMonth
    },
  })
  const [pendingMonth, setPendingMonth] = useState<string | null>(null)

  const applyMonthChange = (newMonth: string): void => {
    if (isSaving) return
    setSaveResult(null)
    setSnapshotResult(null)
    setSaveError(null)
    setShowPreviewModal(false)
    setSelectedMonth(newMonth)
    setAppliedMonth(newMonth)
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('month', newMonth)
      return params
    })
  }

  const handleMonthChange = (newMonth: string): void => {
    if (isSaving || newMonth === appliedMonth) return
    if (isDirty) {
      setPendingMonth(newMonth)
    } else {
      applyMonthChange(newMonth)
    }
  }

  // ── column visibility ──────────────────────────────────────────────────
  const {
    ref: colsDropdownRef,
    open: colsOpen,
    setOpen: setColsOpen,
    hiddenCount,
    isVisible,
    toggleColumn,
  } = useColumnVisibility(HIDDEN_COLS_KEY, COLUMN_DEFS)

  const col = isVisible

  // Keep selectedMonth in sync with query string
  useEffect(() => {
    const urlMonth = searchParams.get('month')
    if (urlMonth && urlMonth !== selectedMonth) {
      setSelectedMonth(urlMonth)
      setAppliedMonth(urlMonth)
    }
  }, [searchParams, selectedMonth])

  // ── queries ──────────────────────────────────────────────────────────────

  const {
    data: activeContractsRaw = [],
    isLoading: loadingContracts,
    isError: contractsError,
  } = useQuery({
    queryKey: ['contracts', 'active', 'monthly'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contracts')
        .select('*, tenant:profiles(id, first_name, last_name)')
        .eq('status', 'active')
        .is('deleted_at', null)
        .order('room_number')
      if (error) throw error
      return data as unknown as MonthlyContractRow[]
    },
  })

  const {
    data: allRoomsRaw = [],
    isLoading: loadingRooms,
    isError: roomsError,
  } = useQuery({
    queryKey: ['rooms', 'all', 'monthly'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .is('deleted_at', null)
        .order('room_number')
      if (error) throw error
      return data
    },
  })

  const {
    data: existingRecordsRaw = [],
    isLoading: loadingRecords,
    isError: recordsError,
  } = useQuery({
    queryKey: ['utility_records', 'monthly', appliedMonth],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('utility_records')
        .select('*')
        .is('deleted_at', null)
        .gte('record_date', monthStart(appliedMonth))
        .lte('record_date', monthEnd(appliedMonth))
      if (error) throw error
      return data
    },
  })

  /** Previous month records to pre-fill prev readings on empty rows */
  const {
    data: prevMonthRecordsRaw = [],
    isLoading: loadingPrevious,
    isError: previousError,
  } = useQuery({
    queryKey: ['utility_records', 'prev_month', appliedMonth],
    queryFn: async () => {
      const [y, m] = appliedMonth.split('-').map(Number)
      const prevDate = new Date(y, m - 2, 1)
      const prevYM = toYearMonth(prevDate)
      const { data, error } = await supabase
        .from('utility_records')
        .select(
          'room_number, elec_curr_reading, water_curr_reading, record_date',
        )
        .is('deleted_at', null)
        .gte('record_date', monthStart(prevYM))
        .lte('record_date', monthEnd(prevYM))
      if (error) throw error
      return data
    },
  })

  /** Count of snapshots for this month — used to show Compare button */
  const { data: snapshotCount = 0 } = useQuery({
    queryKey: ['monthly_snapshots', 'count', appliedMonth],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('monthly_snapshots')
        .select('id', { count: 'exact', head: true })
        .eq('snapshot_month', appliedMonth)
        .is('deleted_at', null)
      if (error) throw error
      return count ?? 0
    },
  })

  /** Snapshot rows for historical room data comparison */
  const {
    data: snapshotData,
    isLoading: loadingSnapshot,
    isError: snapshotError,
  } = useQuery<SnapshotData>({
    queryKey: ['monthly_snapshot_rows', appliedMonth],
    queryFn: async () => {
      // Get the most recent snapshot for this month
      const { data: snapshots, error: snapErr } = await supabase
        .from('monthly_snapshots')
        .select('id')
        .eq('snapshot_month', appliedMonth)
        .is('deleted_at', null)
        .order('snapshotted_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(1)
      if (snapErr) throw snapErr
      if (!snapshots || snapshots.length === 0) return { id: null, rows: [] }

      // Fetch all rows for that snapshot
      const { data, error } = await supabase
        .from('monthly_snapshot_rows')
        .select('*')
        .eq('snapshot_id', snapshots[0].id)
        .is('deleted_at', null)
        .order('room_number')
      if (error) throw error
      return { id: snapshots[0].id, rows: data ?? [] }
    },
  })
  const snapshotRowsRaw = snapshotData?.rows ?? EMPTY_SNAPSHOT_ROWS
  const hasLoadError =
    contractsError ||
    roomsError ||
    recordsError ||
    previousError ||
    snapshotError

  const isLoading =
    loadingContracts ||
    loadingRooms ||
    loadingRecords ||
    loadingPrevious ||
    loadingSnapshot

  // ── build/rebuild rows whenever data changes ───────────────────────────

  useEffect(() => {
    if (isLoading || hasLoadError || isSaving) return

    const newRows: MonthlyRowEntry[] =
      snapshotRowsRaw.length > 0
        ? buildSnapshotMonthlyRows({
            snapshotRows: snapshotRowsRaw,
            rooms: allRoomsRaw,
            contracts: activeContractsRaw,
            existingRecords: existingRecordsRaw,
          })
        : buildLiveMonthlyRows({
            month: appliedMonth,
            rooms: allRoomsRaw,
            contracts: activeContractsRaw,
            existingRecords: existingRecordsRaw,
            prevMonthRecords: prevMonthRecordsRaw,
          })

    setEditor((prev) => {
      if (prev.month === appliedMonth && prev.rows.some((row) => row.dirty))
        return prev
      return {
        month: appliedMonth,
        snapshotId: snapshotData?.id ?? null,
        rows: newRows,
      }
    })
  }, [
    activeContractsRaw,
    allRoomsRaw,
    existingRecordsRaw,
    prevMonthRecordsRaw,
    snapshotRowsRaw,
    isLoading,
    hasLoadError,
    isSaving,
    snapshotData,
    appliedMonth,
  ])

  // ── row update helpers ─────────────────────────────────────────────────

  const updateRow = (
    roomNumber: string,
    updates: Partial<MonthlyUtilityFormValues>,
  ) => {
    setRows((prev) =>
      prev.map((r) =>
        r.room.room_number === roomNumber
          ? { ...r, form: { ...r.form, ...updates }, dirty: true, errors: {} }
          : r,
      ),
    )
  }

  // ── sync utilities mutation ─────────────────────────────────────────────

  const syncUtilitiesMutation = useMutation({
    mutationFn: async (entries: MonthlyRowEntry[]) => {
      let saved = 0
      const errRows: string[] = []

      for (const entry of entries) {
        if (!entry.snapshotForm) continue

        if (entry.existingRecord) {
          const { error } = await supabase
            .from('utility_records')
            .update({
              elec_unit_price: Number(entry.snapshotForm.elec_unit_price) || 0,
              elec_prev_reading:
                Number(entry.snapshotForm.elec_prev_reading) || 0,
              elec_curr_reading:
                Number(entry.snapshotForm.elec_curr_reading) || 0,
              water_unit_price:
                Number(entry.snapshotForm.water_unit_price) || 0,
              water_prev_reading:
                Number(entry.snapshotForm.water_prev_reading) || 0,
              water_curr_reading:
                Number(entry.snapshotForm.water_curr_reading) || 0,
            })
            .eq('id', entry.existingRecord.util.id)
          if (error) errRows.push(entry.room.room_number)
          else saved++
        } else {
          const payload = {
            room_number: entry.room.room_number,
            contract_id: entry.contract?.id ?? null,
            elec_unit_price: Number(entry.snapshotForm.elec_unit_price) || 0,
            elec_prev_reading:
              Number(entry.snapshotForm.elec_prev_reading) || 0,
            elec_curr_reading:
              Number(entry.snapshotForm.elec_curr_reading) || 0,
            water_unit_price: Number(entry.snapshotForm.water_unit_price) || 0,
            water_prev_reading:
              Number(entry.snapshotForm.water_prev_reading) || 0,
            water_curr_reading:
              Number(entry.snapshotForm.water_curr_reading) || 0,
            record_date: entry.snapshotForm.record_date || `${appliedMonth}-01`,
            payment_status:
              entry.snapshotForm.payment_status === 'paid' ? 'paid' : 'unpaid',
            remark: entry.snapshotForm.remark || null,
          }
          const { error } = await supabase
            .from('utility_records')
            .insert(payload)
          if (error) errRows.push(entry.room.room_number)
          else saved++
        }
      }

      return { saved, errors: errRows.length }
    },
    onSuccess: (result) => {
      setSaveResult(result)
      queryClient.invalidateQueries({
        queryKey: ['utility_records', 'monthly', appliedMonth],
      })
      setShowSyncUtilitiesModal(false)
    },
  })

  const saveMutation = useMutation({
    mutationFn: saveMonthlyRecords,
    onSuccess: async (result, plan) => {
      setSaveResult({ saved: result.saved_count, errors: 0 })
      setSnapshotResult({
        snapshotId: result.snapshot_id,
        rowCount: result.row_count,
      })
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['utility_records', 'monthly', plan.month],
        }),
        queryClient.invalidateQueries({
          queryKey: ['monthly_snapshot_rows', plan.month],
        }),
        queryClient.invalidateQueries({
          queryKey: ['monthly_snapshots', 'count', plan.month],
        }),
      ])
      const fresh = queryClient.getQueryData<SnapshotData>([
        'monthly_snapshot_rows',
        plan.month,
      ])
      if (!fresh || fresh.id !== result.snapshot_id) {
        setSaveError(
          'The save completed. Reload to refresh the saved month before editing again.',
        )
        return
      }
      const savedRows = buildSnapshotMonthlyRows({
        snapshotRows: fresh.rows,
        rooms: allRoomsRaw,
        contracts: activeContractsRaw,
        existingRecords:
          queryClient.getQueryData<MonthlyUtilityRow[]>([
            'utility_records',
            'monthly',
            plan.month,
          ]) ?? existingRecordsRaw,
      })
      setEditor((prev) =>
        prev.month !== plan.month
          ? prev
          : {
              month: plan.month,
              snapshotId: result.snapshot_id,
              rows: reconcileSavedRows(
                prev.rows,
                savedRows,
                plan.utilityEdits.map((edit) => edit.room_number),
              ),
            },
      )
    },
  })

  const isDisplayedRow = (row: MonthlyRowEntry) =>
    !normalizedRoomSearch || row.room.room_number === normalizedRoomSearch
  const displayRows = rows.filter(isDisplayedRow)

  const handleSaveAll = () => {
    if (isSaving || isLoading || hasLoadError || editor.month !== appliedMonth)
      return
    let hasError = false
    const checked = rows.map((row) => {
      if (!isDisplayedRow(row) || !row.dirty) return row
      const parsed = rowSchema.safeParse(row.form)
      const errors: Record<string, string> = {}
      if (!parsed.success) {
        hasError = true
        for (const issue of parsed.error.issues)
          errors[String(issue.path[0])] = issue.message
      }
      return { ...row, errors }
    })
    setRows(checked)
    if (hasError) return
    setSaveError(null)
    setPreviewRows(checked.filter(isDisplayedRow))
    setShowPreviewModal(true)
  }

  const handleConfirmSave = async (includeSystemChanges: boolean) => {
    if (isSaving || editor.month !== appliedMonth || !userId) return
    const confirmed = includeSystemChanges
      ? applyRoomFieldsToRows(previewRows, [...ROOM_SYNC_FIELDS])
      : previewRows
    let plan: MonthlySavePlan
    try {
      plan = buildMonthlySavePlan(
        appliedMonth,
        editor.snapshotId,
        rows,
        confirmed,
      )
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Unable to prepare this save',
      )
      setShowPreviewModal(false)
      return
    }
    setShowPreviewModal(false)
    setSaveError(null)
    setSaveResult(null)
    setSnapshotResult(null)
    setIsSaving(true)
    try {
      await saveMutation.mutateAsync(plan)
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Unable to save this month',
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleMarkAllPaid = () => {
    setRows((prev) =>
      prev.map((r) =>
        isDisplayedRow(r)
          ? {
              ...r,
              form: { ...r.form, payment_status: 'paid' as const },
              dirty: true,
            }
          : r,
      ),
    )
  }

  const handlePasteCurr = (
    rowIndex: number,
    field: 'elec_curr_reading' | 'water_curr_reading',
    e: ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault()
    const lines = e.clipboardData
      .getData('text')
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l !== '')
    setRows((prev) => {
      const next = [...prev]
      lines.forEach((line, i) => {
        const idx = rowIndex + i
        if (idx >= next.length) return
        const num = Number(line.replace(/,/g, ''))
        if (Number.isNaN(num)) return
        next[idx] = {
          ...next[idx],
          form: { ...next[idx].form, [field]: String(num) },
          dirty: true,
          errors: {},
        }
      })
      return next
    })
  }

  const handleApplyBulkElecPrice = () => {
    const price = bulkElecPrice.trim()
    if (!price) return
    setRows((prev) =>
      prev.map((r) =>
        isDisplayedRow(r)
          ? {
              ...r,
              form: { ...r.form, elec_unit_price: price },
              dirty: true,
            }
          : r,
      ),
    )
    setBulkElecPrice('')
  }

  const handleApplyBulkWaterPrice = () => {
    const price = bulkWaterPrice.trim()
    if (!price) return
    setRows((prev) =>
      prev.map((r) =>
        isDisplayedRow(r)
          ? {
              ...r,
              form: { ...r.form, water_unit_price: price },
              dirty: true,
            }
          : r,
      ),
    )
    setBulkWaterPrice('')
  }

  const handleLoadFieldFromRoom = () => {
    const fields =
      selectedRoomSyncField === 'all'
        ? [...ROOM_SYNC_FIELDS, 'elec_prev', 'water_prev']
        : [selectedRoomSyncField]

    const roomFieldsToSync = fields.filter((f) =>
      ROOM_SYNC_FIELDS.includes(f as RoomSyncField),
    ) as RoomSyncField[]
    const syncElecPrev = fields.includes('elec_prev')
    const syncWaterPrev = fields.includes('water_prev')

    setRows((prev) => {
      let displayed = prev.filter(isDisplayedRow)

      if (roomFieldsToSync.length > 0) {
        displayed = applyRoomFieldsToRows(displayed, roomFieldsToSync)
      }

      if (syncElecPrev || syncWaterPrev) {
        const prevByRoom = new Map(
          prevMonthRecordsRaw.map((r) => [r.room_number, r]),
        )
        displayed = displayed.map((row) => {
          let updated = false
          const newForm = { ...row.form }
          const prevRec = prevByRoom.get(row.room.room_number)

          if (syncElecPrev) {
            const val =
              prevRec?.elec_curr_reading != null
                ? String(prevRec.elec_curr_reading)
                : ''
            if (newForm.elec_prev_reading !== val) {
              newForm.elec_prev_reading = val
              updated = true
            }
          }
          if (syncWaterPrev) {
            const val =
              prevRec?.water_curr_reading != null
                ? String(prevRec.water_curr_reading)
                : ''
            if (newForm.water_prev_reading !== val) {
              newForm.water_prev_reading = val
              updated = true
            }
          }

          if (updated) {
            return { ...row, form: newForm, dirty: true, errors: {} }
          }
          return row
        })
      }

      const displayedByRoom = new Map(
        displayed.map((row) => [row.room.room_number, row]),
      )
      return prev.map((row) => displayedByRoom.get(row.room.room_number) ?? row)
    })
    setSnapshotResult(null)
  }

  const handleLoadMissingFromSource = (
    fieldsByRoom: Record<string, SnapshotComparableField[]>,
  ) => {
    setRows((prev) => applySourceFieldsToRows(prev, fieldsByRoom))
    setSnapshotResult(null)
    setShowPreviewModal(false)
  }

  const handleEnterKey = (
    e: KeyboardEvent<HTMLInputElement>,
    colName: string,
  ) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    const inputs = document.querySelectorAll<HTMLInputElement>(
      `[data-col="${colName}"]`,
    )
    const list = Array.from(inputs)
    const idx = list.indexOf(e.currentTarget)
    if (idx >= 0 && idx < list.length - 1) {
      list[idx + 1].focus()
      list[idx + 1].select()
    }
  }

  // ── render ─────────────────────────────────────────────────────────────

  const dirtyCount = displayRows.filter((r) => r.dirty).length
  const hasSnapshotRows = displayRows.some((r) => r.source === 'snapshot')

  function renderHeader() {
    return (
      <>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <h2 className="font-semibold text-gray-900 text-lg">
                Monthly Records
              </h2>
              <p className="text-gray-500 text-xs">
                {displayRows.length} room{displayRows.length !== 1 ? 's' : ''} (
                {
                  displayRows.filter((r) => r.form.room_status === 'occupied')
                    .length
                }{' '}
                occupied) ·{' '}
                <span className="font-medium text-gray-700">
                  {appliedMonth}
                </span>
              </p>
            </div>

            <MonthPicker
              id="month-picker"
              value={selectedMonth}
              onValueChange={handleMonthChange}
              className="w-36"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const currentMonth = toYearMonth(new Date())
                handleMonthChange(currentMonth)
              }}
            >
              This Month
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            {snapshotResult && (
              <span className="font-medium text-purple-600">
                Snapshot saved ({snapshotResult.rowCount} rows)
              </span>
            )}
            {saveResult && (
              <span className="font-medium text-green-600">
                Saved {saveResult.saved} record
                {saveResult.saved !== 1 ? 's' : ''}
                {saveResult.errors > 0 && `, ${saveResult.errors} failed`}
              </span>
            )}
            {dirtyCount > 0 && (
              <span className="text-amber-600">
                {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
              </span>
            )}
            <Button
              size="sm"
              onClick={handleSaveAll}
              disabled={
                isSaving ||
                isLoading ||
                hasLoadError ||
                editor.month !== appliedMonth ||
                (dirtyCount === 0 &&
                  displayRows.filter((r) => r.existingRecord).length === 0)
              }
            >
              {isSaving ? 'Saving…' : 'Preview Change'}
            </Button>
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-xs">
          <ColumnVisibilityToggle
            title="Columns"
            columns={COLUMN_DEFS}
            isOpen={colsOpen}
            hiddenCount={hiddenCount}
            wrapperRef={colsDropdownRef}
            isVisible={isVisible}
            onToggle={toggleColumn}
            onToggleOpen={() => setColsOpen((o) => !o)}
          />
          <div className="flex items-center gap-2">
            <label htmlFor="room-search" className="font-medium text-gray-600">
              Search:
            </label>
            <Input
              id="room-search"
              type="search"
              placeholder="101"
              value={roomSearch}
              onChange={(e) => setRoomSearch(e.target.value)}
              className="h-7 w-28 text-xs"
            />
          </div>
          <Button variant="outline" size="sm" onClick={handleMarkAllPaid}>
            Mark All Paid
          </Button>
          <span className="font-medium text-gray-500">Adjust All ฿/Unit:</span>
          <div className="flex items-center gap-1">
            <span className="font-medium text-yellow-700">Elec</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="price"
              value={bulkElecPrice}
              onChange={(e) => setBulkElecPrice(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="h-7 w-20 text-right text-xs"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleApplyBulkElecPrice}
              disabled={!bulkElecPrice}
            >
              Apply
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-blue-700">Water</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="price"
              value={bulkWaterPrice}
              onChange={(e) => setBulkWaterPrice(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="h-7 w-20 text-right text-xs"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleApplyBulkWaterPrice}
              disabled={!bulkWaterPrice}
            >
              Apply
            </Button>
          </div>
          <span className="font-medium text-slate-500">Load room data:</span>
          <select
            aria-label="Room field to load into snapshot rows"
            value={selectedRoomSyncField}
            onChange={(e) =>
              setSelectedRoomSyncField(e.target.value as RoomSyncSelection)
            }
            className="h-7 rounded border border-gray-200 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="all">All</option>
            {ROOM_SYNC_FIELDS.map((field) => (
              <option key={field} value={field}>
                {ROOM_SYNC_FIELD_LABELS[field]}
              </option>
            ))}
            <option value="elec_prev">Elec Prev</option>
            <option value="water_prev">Water Prev</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadFieldFromRoom}
            disabled={!hasSnapshotRows}
          >
            Load From Room
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-purple-600 text-purple-600 hover:bg-purple-50 hover:text-purple-700"
            onClick={() => setShowSyncUtilitiesModal(true)}
            disabled={!hasSnapshotRows}
          >
            Sync Utilities
          </Button>
          {snapshotCount > 1 && (
            <Button
              variant="outline"
              size="sm"
              className="border-purple-600 text-purple-600 hover:bg-purple-50 hover:text-purple-700"
              onClick={() => setShowCompareModal(true)}
            >
              Compare Snapshots
            </Button>
          )}
        </div>
      </>
    )
  }

  return (
    <fieldset
      disabled={isSaving}
      className="relative flex h-full min-w-0 flex-col"
    >
      {showHeader && (
        <div className="sticky top-0 z-30 bg-gray-50">{renderHeader()}</div>
      )}

      <Skeleton
        name="admin-monthly-records"
        loading={isLoading}
        className="flex min-h-0 flex-1 flex-col *:data-boneyard-content:flex *:data-boneyard-content:min-h-0 *:data-boneyard-content:flex-1 *:data-boneyard-content:flex-col"
      >
        {/* Table */}
        <Card className="min-h-0 flex-1 overflow-hidden p-0">
          <div className="h-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="sticky top-0 z-20 bg-gray-50">
                  {/* Room info */}
                  <th className="sticky left-0 z-30 whitespace-nowrap bg-gray-50 px-3 py-3 text-left font-medium text-gray-500">
                    Room
                  </th>
                  {col('tenant') && (
                    <th className="whitespace-nowrap px-3 py-3 text-left font-medium text-gray-500">
                      Tenant
                    </th>
                  )}
                  {col('rent') && (
                    <th className="whitespace-nowrap px-3 py-3 text-right font-medium text-gray-500">
                      Rent (฿)
                    </th>
                  )}
                  {/* Electricity */}
                  {col('elec_unit_price') && (
                    <th className="whitespace-nowrap bg-yellow-50 px-3 py-3 text-right font-medium text-gray-500">
                      ฿/Unit
                      <br />
                      <span className="font-normal text-gray-400 text-xs">
                        Elec
                      </span>
                    </th>
                  )}
                  {col('elec_prev') && (
                    <th className="whitespace-nowrap bg-yellow-50 px-3 py-3 text-right font-medium text-gray-500">
                      Prev
                    </th>
                  )}
                  {col('elec_curr') && (
                    <th className="whitespace-nowrap bg-yellow-50 px-3 py-3 text-right font-medium text-gray-500">
                      Curr
                    </th>
                  )}
                  {col('elec_units') && (
                    <th className="whitespace-nowrap bg-yellow-50 px-3 py-3 text-right font-medium text-gray-400">
                      Units
                      <br />
                      <span className="text-gray-300">(calc)</span>
                    </th>
                  )}
                  {col('elec_total') && (
                    <th className="whitespace-nowrap bg-yellow-50 px-3 py-3 text-right font-medium text-gray-400">
                      Total ฿<br />
                      <span className="text-gray-300">(calc)</span>
                    </th>
                  )}
                  {/* Water */}
                  {col('water_unit_price') && (
                    <th className="whitespace-nowrap bg-blue-50 px-3 py-3 text-right font-medium text-gray-500">
                      ฿/Unit
                      <br />
                      <span className="font-normal text-gray-400 text-xs">
                        Water
                      </span>
                    </th>
                  )}
                  {col('water_prev') && (
                    <th className="whitespace-nowrap bg-blue-50 px-3 py-3 text-right font-medium text-gray-500">
                      Prev
                    </th>
                  )}
                  {col('water_curr') && (
                    <th className="whitespace-nowrap bg-blue-50 px-3 py-3 text-right font-medium text-gray-500">
                      Curr
                    </th>
                  )}
                  {col('water_units') && (
                    <th className="whitespace-nowrap bg-blue-50 px-3 py-3 text-right font-medium text-gray-400">
                      Units
                      <br />
                      <span className="text-gray-300">(calc)</span>
                    </th>
                  )}
                  {col('water_total') && (
                    <th className="whitespace-nowrap bg-blue-50 px-3 py-3 text-right font-medium text-gray-400">
                      Total ฿<br />
                      <span className="text-gray-300">(calc)</span>
                    </th>
                  )}
                  {/* Other fees (from room) */}
                  {col('internet') && (
                    <th className="whitespace-nowrap px-3 py-3 text-right font-medium text-gray-500">
                      Internet
                    </th>
                  )}
                  {col('furniture') && (
                    <th className="whitespace-nowrap px-3 py-3 text-right font-medium text-gray-500">
                      Furniture
                    </th>
                  )}
                  {col('maintenance') && (
                    <th className="whitespace-nowrap px-3 py-3 text-right font-medium text-gray-500">
                      Maintain
                    </th>
                  )}
                  {col('other') && (
                    <th className="whitespace-nowrap px-3 py-3 text-right font-medium text-gray-500">
                      Other
                    </th>
                  )}
                  {col('other_fee_label') && (
                    <th className="whitespace-nowrap px-3 py-3 text-left font-medium text-gray-500">
                      Other Fee Label
                    </th>
                  )}
                  {/* Grand total */}
                  {col('grand_total') && (
                    <th className="whitespace-nowrap bg-green-50 px-3 py-3 text-right font-medium text-gray-500">
                      Grand Total ฿<br />
                      <span className="font-normal text-gray-300 text-xs">
                        (calc)
                      </span>
                    </th>
                  )}
                  {/* Record date, payment, remark */}
                  {col('date') && (
                    <th className="whitespace-nowrap px-3 py-3 text-left font-medium text-gray-500">
                      Date
                    </th>
                  )}
                  {col('payment') && (
                    <th className="whitespace-nowrap px-3 py-3 text-left font-medium text-gray-500">
                      Payment
                    </th>
                  )}
                  {col('remark') && (
                    <th className="whitespace-nowrap px-3 py-3 text-left font-medium text-gray-500">
                      Remark
                    </th>
                  )}
                  {/* Status badge */}
                  {col('status') && (
                    <th className="whitespace-nowrap px-3 py-3 text-left font-medium text-gray-500">
                      Status
                    </th>
                  )}
                  {/* Room metadata (read-only) */}
                  {col('room_type') && (
                    <th className="whitespace-nowrap bg-slate-50 px-3 py-3 text-left font-medium text-gray-500">
                      Room Type
                    </th>
                  )}
                  {col('room_status') && (
                    <th className="whitespace-nowrap bg-slate-50 px-3 py-3 text-left font-medium text-gray-500">
                      Room Status
                    </th>
                  )}
                  {col('room_remark') && (
                    <th className="whitespace-nowrap bg-slate-50 px-3 py-3 text-left font-medium text-gray-500">
                      Room Note
                    </th>
                  )}
                  {col('repair_details') && (
                    <th className="whitespace-nowrap bg-slate-50 px-3 py-3 text-left font-medium text-gray-500">
                      Repair Details
                    </th>
                  )}
                  {col('keycards') && (
                    <th className="whitespace-nowrap bg-slate-50 px-3 py-3 text-left font-medium text-gray-500">
                      Keycards
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {displayRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        1 + COLUMN_DEFS.filter((c) => isVisible(c.id)).length
                      }
                      className="px-4 py-8 text-center text-gray-400"
                    >
                      No rooms found.
                    </td>
                  </tr>
                )}
                {displayRows.map((entry) => {
                  const index = rows.findIndex(
                    (row) => row.room.room_number === entry.room.room_number,
                  )
                  const totals = calcTotals(entry.form)
                  const hasErrors = Object.keys(entry.errors).length > 0
                  const isNew = entry.source === 'live' && !entry.existingRecord

                  // Real-time rollover validation for display
                  const elecMeterErr =
                    entry.form.elec_prev_reading &&
                    entry.form.elec_curr_reading &&
                    !isMeterReadingValid(
                      Number(entry.form.elec_prev_reading),
                      Number(entry.form.elec_curr_reading),
                    )
                      ? 'Reading decreased (not a rollover)'
                      : null
                  const waterMeterErr =
                    entry.form.water_prev_reading &&
                    entry.form.water_curr_reading &&
                    !isMeterReadingValid(
                      Number(entry.form.water_prev_reading),
                      Number(entry.form.water_curr_reading),
                    )
                      ? 'Reading decreased (not a rollover)'
                      : null

                  return (
                    <tr
                      key={entry.room.room_number}
                      className={[
                        'hover:bg-gray-50',
                        entry.dirty ? 'bg-amber-50/40' : '',
                        hasErrors ? 'bg-red-50/40' : '',
                      ].join(' ')}
                    >
                      {/* Room */}
                      <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-2 font-medium">
                        {entry.room.room_number}
                      </td>
                      {/* Tenant */}
                      {col('tenant') && (
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                          {entry.tenantName ? (
                            entry.tenantName
                          ) : entry.form.room_status === 'available' ? (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-400 text-xs">
                              Vacant
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                      {/* Rent */}
                      {col('rent') && (
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="price_monthly"
                            value={entry.form.price_monthly}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                price_monthly: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'price_monthly')
                            }
                            className="h-7 w-24 text-right text-xs"
                          />
                        </td>
                      )}

                      {/* Elec unit price */}
                      {col('elec_unit_price') && (
                        <td className="bg-yellow-50/50 px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            data-col="elec_unit_price"
                            value={entry.form.elec_unit_price}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                elec_unit_price: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'elec_unit_price')
                            }
                            className="h-7 w-20 text-right text-xs"
                          />
                        </td>
                      )}
                      {/* Elec prev */}
                      {col('elec_prev') && (
                        <td className="bg-yellow-50/50 px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="elec_prev_reading"
                            value={entry.form.elec_prev_reading}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                elec_prev_reading: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'elec_prev_reading')
                            }
                            className="h-7 w-24 text-right text-xs"
                          />
                        </td>
                      )}
                      {/* Elec curr */}
                      {col('elec_curr') && (
                        <td className="bg-yellow-50/50 px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="elec_curr_reading"
                            value={entry.form.elec_curr_reading}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                elec_curr_reading: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'elec_curr_reading')
                            }
                            onPaste={(e) =>
                              handlePasteCurr(index, 'elec_curr_reading', e)
                            }
                            className="h-7 w-24 text-right text-xs"
                          />
                          {(entry.errors.elec_curr_reading || elecMeterErr) && (
                            <p className="mt-0.5 text-red-500 text-xs">
                              {entry.errors.elec_curr_reading || elecMeterErr}
                            </p>
                          )}
                        </td>
                      )}
                      {/* Elec units (calc) */}
                      {col('elec_units') && (
                        <td className="bg-yellow-50/50 px-3 py-2 text-right text-gray-500 tabular-nums">
                          {entry.form.elec_curr_reading !== '' ? (
                            totals.elec_units
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      )}
                      {/* Elec total (calc) */}
                      {col('elec_total') && (
                        <td className="bg-yellow-50/50 px-3 py-2 text-right font-medium tabular-nums">
                          {entry.form.elec_curr_reading !== '' ? (
                            totals.elec_total.toLocaleString()
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      )}

                      {/* Water unit price */}
                      {col('water_unit_price') && (
                        <td className="bg-blue-50/50 px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            data-col="water_unit_price"
                            value={entry.form.water_unit_price}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                water_unit_price: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'water_unit_price')
                            }
                            className="h-7 w-20 text-right text-xs"
                          />
                        </td>
                      )}
                      {/* Water prev */}
                      {col('water_prev') && (
                        <td className="bg-blue-50/50 px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="water_prev_reading"
                            value={entry.form.water_prev_reading}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                water_prev_reading: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'water_prev_reading')
                            }
                            className="h-7 w-24 text-right text-xs"
                          />
                        </td>
                      )}
                      {/* Water curr */}
                      {col('water_curr') && (
                        <td className="bg-blue-50/50 px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="water_curr_reading"
                            value={entry.form.water_curr_reading}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                water_curr_reading: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'water_curr_reading')
                            }
                            onPaste={(e) =>
                              handlePasteCurr(index, 'water_curr_reading', e)
                            }
                            className="h-7 w-24 text-right text-xs"
                          />
                          {(entry.errors.water_curr_reading ||
                            waterMeterErr) && (
                            <p className="mt-0.5 text-red-500 text-xs">
                              {entry.errors.water_curr_reading || waterMeterErr}
                            </p>
                          )}
                        </td>
                      )}
                      {/* Water units (calc) */}
                      {col('water_units') && (
                        <td className="bg-blue-50/50 px-3 py-2 text-right text-gray-500 tabular-nums">
                          {entry.form.water_curr_reading !== '' ? (
                            totals.water_units
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      )}
                      {/* Water total (calc) */}
                      {col('water_total') && (
                        <td className="bg-blue-50/50 px-3 py-2 text-right font-medium tabular-nums">
                          {entry.form.water_curr_reading !== '' ? (
                            totals.water_total.toLocaleString()
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      )}

                      {/* Other room fees (editable) */}
                      {col('internet') && (
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="internet_fee"
                            value={entry.form.internet_fee}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                internet_fee: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) => handleEnterKey(e, 'internet_fee')}
                            className="h-7 w-20 text-right text-xs"
                          />
                        </td>
                      )}
                      {col('furniture') && (
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="furniture_fee"
                            value={entry.form.furniture_fee}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                furniture_fee: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'furniture_fee')
                            }
                            className="h-7 w-20 text-right text-xs"
                          />
                        </td>
                      )}
                      {col('maintenance') && (
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="maintenance_fee"
                            value={entry.form.maintenance_fee}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                maintenance_fee: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'maintenance_fee')
                            }
                            className="h-7 w-20 text-right text-xs"
                          />
                        </td>
                      )}
                      {col('other') && (
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            data-col="other_fee"
                            value={entry.form.other_fee}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                other_fee: e.target.value,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) => handleEnterKey(e, 'other_fee')}
                            className="h-7 w-20 text-right text-xs"
                          />
                        </td>
                      )}
                      {col('other_fee_label') && (
                        <td className="px-3 py-2">
                          <Input
                            type="text"
                            data-col="other_fee_label"
                            value={entry.form.other_fee_label}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                other_fee_label: e.target.value,
                              })
                            }
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'other_fee_label')
                            }
                            placeholder="—"
                            className="h-7 w-28 text-xs"
                          />
                        </td>
                      )}

                      {/* Grand total (calc) */}
                      {col('grand_total') && (
                        <td className="bg-green-50/50 px-3 py-2 text-right font-semibold tabular-nums">
                          {totals.grand_total.toLocaleString()}
                        </td>
                      )}

                      {/* Record date */}
                      {col('date') && (
                        <td className="px-3 py-2">
                          <DatePicker
                            data-col="record_date"
                            id={`record_date_${entry.room.room_number}`}
                            value={entry.form.record_date}
                            onValueChange={(value) =>
                              updateRow(entry.room.room_number, {
                                record_date: value,
                              })
                            }
                            className="h-7 w-36 text-xs"
                          />
                        </td>
                      )}

                      {/* Payment status */}
                      {col('payment') && (
                        <td className="px-3 py-2">
                          <select
                            aria-label={`Payment status for room ${entry.room.room_number}`}
                            value={entry.form.payment_status}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                payment_status: e.target.value as
                                  | 'paid'
                                  | 'unpaid',
                              })
                            }
                            className="rounded border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                          >
                            <option value="unpaid">unpaid</option>
                            <option value="paid">paid</option>
                          </select>
                        </td>
                      )}

                      {/* Remark */}
                      {col('remark') && (
                        <td className="px-3 py-2">
                          <Input
                            type="text"
                            data-col="remark"
                            value={entry.form.remark}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                remark: e.target.value,
                              })
                            }
                            onKeyDown={(e) => handleEnterKey(e, 'remark')}
                            placeholder="—"
                            className="h-7 w-28 text-xs"
                          />
                        </td>
                      )}

                      {/* Status */}
                      {col('status') && (
                        <td className="whitespace-nowrap px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {/* Source badge */}
                            {entry.source === 'snapshot' ? (
                              <span
                                title="Data loaded from a saved snapshot"
                                className="inline-flex items-center gap-0.5 rounded-full bg-purple-100 px-2 py-0.5 font-semibold text-purple-700 text-xs"
                              >
                                📷 snapshot
                              </span>
                            ) : (
                              <span
                                title="Data loaded from live room / utility records"
                                className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 font-semibold text-sky-600 text-xs"
                              >
                                live
                              </span>
                            )}
                            {/* Record state badge */}
                            {isNew ? (
                              <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-500 text-xs">
                                new
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 font-semibold text-green-700 text-xs">
                                saved
                              </span>
                            )}
                            {entry.dirty && (
                              <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700 text-xs">
                                edited
                              </span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Room metadata */}
                      {col('room_type') && (
                        <td className="bg-slate-50/50 px-3 py-2">
                          <select
                            value={entry.form.room_type}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                room_type: e.target.value,
                              })
                            }
                            className="rounded border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                          >
                            {ROOM_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      {col('room_status') && (
                        <td className="bg-slate-50/50 px-3 py-2">
                          <select
                            value={entry.form.room_status}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                room_status: e.target.value,
                              })
                            }
                            className="rounded border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                          >
                            {ROOM_STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      {col('room_remark') && (
                        <td className="bg-slate-50/50 px-3 py-2">
                          <Input
                            type="text"
                            data-col="room_remark"
                            value={entry.form.room_remark}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                room_remark: e.target.value,
                              })
                            }
                            onKeyDown={(e) => handleEnterKey(e, 'room_remark')}
                            placeholder="—"
                            className="h-7 w-36 text-xs"
                          />
                        </td>
                      )}
                      {col('repair_details') && (
                        <td className="bg-slate-50/50 px-3 py-2">
                          <Input
                            type="text"
                            data-col="repair_details"
                            value={entry.form.repair_details}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                repair_details: e.target.value,
                              })
                            }
                            onKeyDown={(e) =>
                              handleEnterKey(e, 'repair_details')
                            }
                            placeholder="—"
                            className="h-7 w-36 text-xs"
                          />
                        </td>
                      )}
                      {col('keycards') && (
                        <td className="bg-slate-50/50 px-3 py-2">
                          <Input
                            type="text"
                            data-col="keycards"
                            value={entry.form.keycards}
                            onChange={(e) =>
                              updateRow(entry.room.room_number, {
                                keycards: e.target.value,
                              })
                            }
                            onKeyDown={(e) => handleEnterKey(e, 'keycards')}
                            placeholder="A1, A2"
                            className="h-7 w-32 text-xs"
                          />
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>

              {/* Summary footer */}
              {rows.length > 0 && (
                <tfoot className="border-gray-200 border-t-2 bg-gray-50">
                  <tr className="font-semibold text-gray-700">
                    <td
                      colSpan={col('tenant') ? 2 : 1}
                      className="sticky left-0 z-10 whitespace-nowrap bg-gray-50 px-3 py-3 text-left text-gray-500 text-xs uppercase tracking-wide"
                    >
                      Total ({rows.length} rooms)
                    </td>
                    {/* Rent total */}
                    {col('rent') && (
                      <td className="px-3 py-3 text-right tabular-nums">
                        {rows
                          .reduce(
                            (s, r) => s + (Number(r.form.price_monthly) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </td>
                    )}
                    {/* Elec cols */}
                    {col('elec_unit_price') && (
                      <td className="bg-yellow-50/50 px-3 py-3" />
                    )}
                    {col('elec_prev') && (
                      <td className="bg-yellow-50/50 px-3 py-3" />
                    )}
                    {col('elec_curr') && (
                      <td className="bg-yellow-50/50 px-3 py-3" />
                    )}
                    {col('elec_units') && (
                      <td className="bg-yellow-50/50 px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .filter((r) => r.form.elec_curr_reading !== '')
                          .reduce(
                            (s, r) => s + calcTotals(r.form).elec_units,
                            0,
                          )}
                      </td>
                    )}
                    {col('elec_total') && (
                      <td className="bg-yellow-50/50 px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .filter((r) => r.form.elec_curr_reading !== '')
                          .reduce(
                            (s, r) => s + calcTotals(r.form).elec_total,
                            0,
                          )
                          .toLocaleString()}
                      </td>
                    )}
                    {/* Water cols */}
                    {col('water_unit_price') && (
                      <td className="bg-blue-50/50 px-3 py-3" />
                    )}
                    {col('water_prev') && (
                      <td className="bg-blue-50/50 px-3 py-3" />
                    )}
                    {col('water_curr') && (
                      <td className="bg-blue-50/50 px-3 py-3" />
                    )}
                    {col('water_units') && (
                      <td className="bg-blue-50/50 px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .filter((r) => r.form.water_curr_reading !== '')
                          .reduce(
                            (s, r) => s + calcTotals(r.form).water_units,
                            0,
                          )}
                      </td>
                    )}
                    {col('water_total') && (
                      <td className="bg-blue-50/50 px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .filter((r) => r.form.water_curr_reading !== '')
                          .reduce(
                            (s, r) => s + calcTotals(r.form).water_total,
                            0,
                          )
                          .toLocaleString()}
                      </td>
                    )}
                    {/* Internet, furniture, maintenance, other */}
                    {col('internet') && (
                      <td className="px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .reduce(
                            (s, r) => s + (Number(r.form.internet_fee) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </td>
                    )}
                    {col('furniture') && (
                      <td className="px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .reduce(
                            (s, r) => s + (Number(r.form.furniture_fee) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </td>
                    )}
                    {col('maintenance') && (
                      <td className="px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .reduce(
                            (s, r) => s + (Number(r.form.maintenance_fee) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </td>
                    )}
                    {col('other') && (
                      <td className="px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .reduce(
                            (s, r) => s + (Number(r.form.other_fee) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </td>
                    )}
                    {col('other_fee_label') && <td className="px-3 py-3" />}
                    {/* Grand total */}
                    {col('grand_total') && (
                      <td className="bg-green-50/50 px-3 py-3 text-right tabular-nums">
                        {displayRows
                          .reduce(
                            (s, r) => s + calcTotals(r.form).grand_total,
                            0,
                          )
                          .toLocaleString()}
                      </td>
                    )}
                    {/* date, payment, remark, status, room metadata */}
                    {(col('date') ||
                      col('payment') ||
                      col('remark') ||
                      col('status') ||
                      col('room_type') ||
                      col('room_status') ||
                      col('room_remark') ||
                      col('repair_details') ||
                      col('keycards')) && (
                      <td
                        colSpan={
                          (col('date') ? 1 : 0) +
                          (col('payment') ? 1 : 0) +
                          (col('remark') ? 1 : 0) +
                          (col('status') ? 1 : 0) +
                          (col('room_type') ? 1 : 0) +
                          (col('room_status') ? 1 : 0) +
                          (col('room_remark') ? 1 : 0) +
                          (col('repair_details') ? 1 : 0) +
                          (col('keycards') ? 1 : 0)
                        }
                      />
                    )}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      </Skeleton>

      {(saveError || hasLoadError) && (
        <p role="alert" className="text-red-700 text-sm">
          {saveError ??
            'Unable to load the complete month. Reload before saving.'}
        </p>
      )}

      {showPreviewModal && (
        <SavePreviewModal
          rows={previewRows}
          snapshotRoomCount={rows.length}
          onConfirm={handleConfirmSave}
          onCancel={() => setShowPreviewModal(false)}
          onLoadMissingFromSource={handleLoadMissingFromSource}
          isPending={isSaving}
        />
      )}

      {showSyncUtilitiesModal && (
        <SyncUtilitiesPreviewModal
          rows={displayRows}
          onConfirm={(syncRows) => syncUtilitiesMutation.mutate(syncRows)}
          onCancel={() => setShowSyncUtilitiesModal(false)}
          isPending={syncUtilitiesMutation.isPending}
        />
      )}

      {showCompareModal && (
        <SnapshotCompareModal
          month={appliedMonth}
          onClose={() => setShowCompareModal(false)}
        />
      )}

      <UnsavedChangesDialog
        isOpen={isBlocked}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to leave without saving? Your changes will be lost."
        confirmText="Leave without saving"
        cancelText="Stay on page"
        onConfirm={proceed}
        onCancel={reset}
      />

      <UnsavedChangesDialog
        isOpen={pendingMonth !== null}
        title="Unsaved Changes in Current Month"
        description="You have unsaved changes in this month. If you switch months, your unsaved changes will be lost. Are you sure you want to proceed?"
        confirmText="Discard and switch"
        cancelText="Stay here"
        onConfirm={() => {
          if (pendingMonth) {
            applyMonthChange(pendingMonth)
            setPendingMonth(null)
          }
        }}
        onCancel={() => {
          setPendingMonth(null)
          setSelectedMonth(appliedMonth)
        }}
      />
    </fieldset>
  )
}
